import React, { useState, useEffect } from 'react';
import { apiCall } from '../services/api';
import RecruiterLetterViewer from './RecruiterLetterViewer';

const RecruiterLettersView = () => {
  const [letters, setLetters]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedLetter, setSelectedLetter] = useState(null);

  useEffect(() => { fetchMyLetters(); }, []);

  const fetchMyLetters = async () => {
    try {
      const res = await apiCall('/letters/my-letters');
      setLetters(res.data || []);
    } catch (err) {
      console.error('Error fetching recruiter letters:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLetter = async (deliveryId) => {
    try {
      const res = await apiCall(`/letters/deliveries/${deliveryId}`);
      setSelectedLetter(res.data);
      fetchMyLetters(); // refresh read-status badge
    } catch (err) {
      console.error('Error opening letter:', err);
    }
  };

  return (
    <div className="main-content">

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Official Engagement Letters</h1>
          <p className="page-subtitle">
            Access and download your official letters issued by the Office of the Vice-Chancellor &amp; HR.
          </p>
        </div>
        {letters.length > 0 && (
          <div className="page-header-actions">
            <div style={styles.countPill}>
              {letters.filter(l => l.status !== 'viewed').length} unread
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      {loading ? (
        <div style={styles.emptyState}>
          <div style={styles.spinner} />
          <p style={{ color: 'var(--text-secondary)', marginTop: 12 }}>Loading your letters…</p>
        </div>
      ) : letters.length === 0 ? (
        <div style={styles.emptyState}>
          <span style={styles.emptyIcon}>📭</span>
          <div style={styles.emptyTitle}>No Official Letters Yet</div>
          <div style={styles.emptySubtitle}>
            Any letters issued to your profile by HR or Admin will appear here.
          </div>
        </div>
      ) : (
        <div style={styles.grid}>
          {letters.map(item => {
            const isNew = item.status !== 'viewed';
            return (
              <div key={item.id} style={{ ...styles.card, ...(isNew ? styles.cardNew : {}) }}>

                {/* Card top row */}
                <div style={styles.cardTopRow}>
                  <span style={styles.refTag}>{item.ref_number}</span>
                  <span style={isNew ? styles.badgeNew : styles.badgeSeen}>
                    {isNew ? '● New' : 'Viewed'}
                  </span>
                </div>

                {/* Title / Subject */}
                <div style={styles.cardTitle}>{item.title}</div>
                <div style={styles.cardSubject}>{item.subject}</div>

                {/* Meta */}
                <div style={styles.cardMeta}>
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Issued</span>
                    <span style={styles.metaValue}>
                      {new Date(item.issue_date).toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                    </span>
                  </div>
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Signatory</span>
                    <span style={styles.metaValue}>{item.signatory_title}</span>
                  </div>
                </div>

                {/* Action */}
                <div style={styles.cardFooter}>
                  <button
                    onClick={() => handleOpenLetter(item.id)}
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    👁️ View / Download PDF
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Letter viewer modal */}
      {selectedLetter && (
        <RecruiterLetterViewer
          letter={selectedLetter}
          onClose={() => setSelectedLetter(null)}
        />
      )}
    </div>
  );
};

const styles = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },

  pageHeader: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 12,
    paddingBottom: 20, borderBottom: '1px solid var(--border-color)',
  },
  pageTitleRow: { display: 'flex', alignItems: 'flex-start', gap: 12 },
  pageIcon: { fontSize: 26, marginTop: 2 },
  pageTitle: { fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  pageSubtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' },

  countPill: {
    alignSelf: 'center',
    background: '#d1fae5', color: '#065f46',
    borderRadius: 99, padding: '5px 14px',
    fontSize: 12, fontWeight: 700,
  },

  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '64px 24px',
    background: '#fafbfc', borderRadius: 12,
    border: '2px dashed var(--border-color)',
    textAlign: 'center',
  },
  emptyIcon:     { fontSize: 48, marginBottom: 12 },
  emptyTitle:    { fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' },
  emptySubtitle: { fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, maxWidth: 360 },

  spinner: {
    width: 36, height: 36, borderRadius: '50%',
    border: '3px solid #e2e8f0',
    borderTopColor: 'var(--primary-color)',
    animation: 'spin 0.8s linear infinite',
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 18,
  },

  card: {
    background: '#fff', borderRadius: 12,
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow-sm)',
    padding: 20, display: 'flex', flexDirection: 'column', gap: 12,
    transition: 'box-shadow 0.2s',
  },
  cardNew: {
    border: '1.5px solid #6ee7b7',
    boxShadow: '0 0 0 3px rgba(16,185,129,0.07)',
  },

  cardTopRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  refTag: {
    fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
    color: '#037b90', background: '#e0f7fa',
    padding: '2px 9px', borderRadius: 4,
  },
  badgeNew: {
    fontSize: 11, fontWeight: 700, color: '#059669',
    background: '#d1fae5', padding: '3px 10px', borderRadius: 99,
  },
  badgeSeen: {
    fontSize: 11, fontWeight: 600, color: '#64748b',
    background: '#f1f5f9', padding: '3px 10px', borderRadius: 99,
  },

  cardTitle: {
    fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.4,
  },
  cardSubject: {
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.04em', color: 'var(--text-secondary)',
    lineHeight: 1.5,
  },

  cardMeta: {
    display: 'flex', gap: 20, flexWrap: 'wrap',
    paddingTop: 12, borderTop: '1px solid var(--border-light)',
  },
  metaItem:  { display: 'flex', flexDirection: 'column', gap: 2 },
  metaLabel: { fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' },
  metaValue: { fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' },

  cardFooter: { marginTop: 'auto' },
};

export default RecruiterLettersView;
